import { Button } from "@/components/ui/button";
import { Phone, Mail, Menu, X, User, ArrowRight, LogIn } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";

export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, loading } = useAuth();

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border/40">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-extrabold tracking-tight text-foreground">
                agents<span className="text-primary">capital</span>
              </span>
            </Link>

            {/* Desktop nav - centered links */}
            <nav className="hidden lg:flex items-center justify-center flex-1 gap-8 mx-8">
              <Link to="/how-it-works" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors">
                How it works
              </Link>
              <Link to="/what-we-pay" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors">
                What we pay
              </Link>
              <Link to="/help" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors">
                Help
              </Link>
              <Link to="/insights" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors">
                Insights
              </Link>
              <Link to="/contact" className="text-sm font-semibold text-foreground/70 hover:text-foreground transition-colors">
                Contact Us
              </Link>
            </nav>

            {/* Right side - CTAs */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/contact">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-2 border-border text-foreground hover:bg-foreground hover:text-background uppercase text-xs tracking-wider px-5 rounded-full font-bold"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Book a Call
                </Button>
              </Link>
              {!loading && user ? (
                <Link to="/account">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-2 border-border text-foreground hover:bg-foreground hover:text-background uppercase text-xs tracking-wider px-5 rounded-full font-bold"
                  >
                    <User className="h-3.5 w-3.5" />
                    My Account
                  </Button>
                </Link>
              ) : !loading ? (
                <>
                  <Link to="/login">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-2 border-border text-foreground hover:bg-foreground hover:text-background uppercase text-xs tracking-wider px-5 rounded-full font-bold"
                    >
                      <LogIn className="h-3.5 w-3.5" />
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-2 border-border text-foreground hover:bg-foreground hover:text-background uppercase text-xs tracking-wider px-5 rounded-full font-bold"
                    >
                      <User className="h-3.5 w-3.5" />
                      Register
                    </Button>
                  </Link>
                </>
              ) : null}
              <Link to="/submit-deal">
                <Button
                  size="sm"
                  className="bg-primary border-2 border-primary text-primary-foreground hover:bg-primary/90 hover:border-primary/90 uppercase text-xs tracking-wider px-6 rounded-full font-bold"
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                  Sell your Deal
                </Button>
              </Link>
            </div>

            {/* Mobile: CTA + hamburger */}
            <div className="flex lg:hidden items-center gap-3">
              <Link to="/submit-deal">
                <Button
                  size="sm"
                  className="bg-foreground border-2 border-foreground text-background hover:bg-foreground/90 uppercase text-xs tracking-wider px-5 rounded-full font-bold"
                >
                  Sell your Deal
                </Button>
              </Link>
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 hover:bg-muted rounded-full transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6 text-foreground" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Full Screen Mobile Menu Overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100] bg-foreground text-background animate-fade-in">
          <div className="container mx-auto px-4 h-full">
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-8 right-8 p-2 hover:bg-background/10 rounded-full transition-colors"
              aria-label="Close menu"
            >
              <X className="h-8 w-8 text-background" />
            </button>

            <div className="flex flex-col justify-center h-full pt-28 pb-20">
              <div className="grid md:grid-cols-2 gap-16 lg:gap-24 max-w-6xl mx-auto w-full">
                <div>
                  <h3 className="text-xs uppercase tracking-widest text-background/60 mb-8 font-bold">
                    How Can We Help
                  </h3>
                  <nav className="space-y-6">
                    {[
                      { label: "How It Works", path: "/how-it-works" },
                      { label: "Sell Your Deal", path: "/submit-deal" },
                      { label: "What We Pay", path: "/what-we-pay" },
                      { label: "Help Centre", path: "/help" },
                      { label: "Contact Us", path: "/contact" },
                      { label: "Insights", path: "/insights" },
                    ].map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMenuOpen(false)}
                        className="block text-3xl md:text-4xl font-extrabold text-background hover:text-primary transition-colors tracking-tight"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                </div>

                <div className="space-y-12">
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-background/60 mb-8 font-bold">
                      More
                    </h3>
                    <nav className="space-y-4">
                      {!user && (
                        <Link to="/register" onClick={() => setMenuOpen(false)} className="block text-lg text-background hover:text-primary transition-colors font-medium">
                          Register
                        </Link>
                      )}
                      <Link to={user ? "/account" : "/login"} onClick={() => setMenuOpen(false)} className="block text-lg text-background hover:text-primary transition-colors font-medium">
                        {user ? "My Account" : "Sign In"}
                      </Link>
                    </nav>
                  </div>

                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-background/60 mb-6 font-bold">
                      Get In Touch
                    </h3>
                    <div className="space-y-3 text-background/90">
                      <a href="tel:02046349406" className="flex items-center gap-2 hover:text-primary transition-colors">
                        <Phone className="h-4 w-4" />
                        <span>Tel: 0204 634 9406</span>
                      </a>
                      <a href="mailto:hello@agentscapital.co.uk" className="flex items-center gap-2 hover:text-primary transition-colors">
                        <Mail className="h-4 w-4" />
                        <span>Email: hello@agentscapital.co.uk</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/submit-deal" onClick={() => setMenuOpen(false)}>
                  <Button
                    size="lg"
                    className="bg-primary border-2 border-primary text-primary-foreground hover:bg-primary-hover hover:border-primary-hover uppercase text-sm tracking-wider px-12 py-6 rounded-2xl font-bold"
                  >
                    Sell your Deal
                  </Button>
                </Link>
                <Link to={user ? "/account" : "/login"} onClick={() => setMenuOpen(false)}>
                  <Button
                    size="lg"
                    className="bg-transparent border-2 border-background text-background hover:bg-background hover:text-foreground uppercase text-sm tracking-wider px-12 py-6 rounded-2xl font-bold"
                  >
                    {user ? "My Account" : "Sign In"}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
